<template>
  <div>
    <!-- Título E-mails de comunicação -->
    <div class="c-row">
      <div class="c-col-full ui-mb-4 c-d-flex c-vertical-end">
        <div class="c-text-h3 ui-flex-1">
          <c-skeleton-block
            v-if="loading"
            width="12rem"
          />
          <span v-else>
            E-mails de comunicação
          </span>
        </div>
        <!-- Botão Incluir e-mail -->
        <div
          v-if="loading"
          class="c-d-flex ui-h-4 ui-space-x-2"
        >
          <c-skeleton-circle size="1rem" />
          <c-skeleton-block width="6rem" />
        </div>
        <button
          v-else
          @click="showAddEmailModal"
          data-cy="addCommunicationEmailButton"
        >
          <c-link
            class="c-text-gray-300 c-d-flex"
            component="button"
          >
            <icon
              name="Edit"
              color="var(--c-color-gray-300)"
            />
            Incluir e-mail
          </c-link>
        </button>
      </div>
    </div>

    <!-- Card E-mails de comunicação -->
    <div class="c-row">
      <div class="c-col-full">
        <card>
          <!-- Table Header -->
          <div class="c-d-flex ui-p-3">
            <div class="ui-flex-1 ui-pr-4">
              <span class="c-text-gray c-text-b2 c-text-gray-600 font-medium">Email</span>
            </div>
            <div class="ui-w-32 ui-pr-4">
              <span class="c-text-gray c-text-b2 c-text-gray-600 font-medium">Inclusão</span>
            </div>
            <div class="ui-w-32 ui-pr-4">
              <span class="c-text-gray c-text-b2 c-text-gray-600 font-medium">Ativação</span>
            </div>
            <div class="ui-w-24 ui-pr-4">
              <span class="c-text-gray c-text-b2 c-text-gray-600 font-medium">Status</span>
            </div>
            <div class="ui-w-16" />
          </div>

          <!-- Loading State -->
          <div
            v-if="emailsLoading"
            class="c-d-flex ui-p-3"
          >
            <div class="ui-w-full">
              <c-skeleton-block
                height="1rem"
                width="100%"
              />
            </div>
          </div>

          <!-- Error State -->
          <div
            v-else-if="emailsError"
            class="c-d-flex ui-p-6 ui-text-center"
          >
            <div class="ui-w-full">
              <p class="c-text-b2 c-text-red-500 ui-mb-2">
                {{ emailsError }}
              </p>
              <button
                @click="loadCommunicationEmails"
                class="c-button c-button--sm c-button--outline ui-mx-auto ui-block"
              >
                Tentar novamente
              </button>
            </div>
          </div>

          <!-- Email List -->
          <div v-else>
            <!-- User's main email (always first) -->
            <div class="c-d-flex ui-p-3 ui-bg-gray-50">
              <div class="ui-flex-1 ui-pr-4">
                <span class="c-text-b1 font-semibold">{{ userEmail }}</span>
              </div>
              <div class="ui-w-32 ui-pr-4">
                <span class="c-text-b2 c-text-gray-400">-</span>
              </div>
              <div class="ui-w-32 ui-pr-4">
                <span class="c-text-b2 c-text-gray-400">-</span>
              </div>
              <div class="ui-w-24 ui-pr-4">
                <div class="c-d-flex c-items-center">
                  <span class="c-text-b2 c-text-green-600 c-d-flex c-items-center">
                    <icon
                      name="Check"
                      color="var(--c-color-green-600)"
                      class="ui-mr-1"
                    />
                    Ativo
                  </span>
                </div>
              </div>
              <div class="ui-w-16 ui-ml-2" />
            </div>

            <!-- Communication emails -->
            <div
              v-for="(email, index) in communicationEmails"
              :key="email.id"
              class="c-d-flex ui-p-3"
            >
              <div class="ui-flex-1 ui-pr-4">
                <span class="c-text-b1">{{ email.email }}</span>
              </div>
              <div class="ui-w-32 ui-pr-4">
                <span class="c-text-b2 c-text-gray-600">{{ email.inclusionDate || '-' }}</span>
              </div>
              <div class="ui-w-32 ui-pr-4">
                <span class="c-text-b2 c-text-gray-600">{{ email.activatedAt || '-' }}</span>
              </div>
              <div class="ui-w-24 ui-pr-4">
                <div class="c-d-flex c-items-center">
                  <span
                    v-if="email.isActive && !email.isPending"
                    class="c-text-b2 c-text-green-600 c-d-flex c-items-center"
                  >
                    <icon
                      name="Check"
                      color="var(--c-color-green-600)"
                      class="ui-mr-1"
                    />
                    Ativo
                  </span>
                  <span
                    v-else-if="email.isPending"
                    class="c-text-b2 c-text-orange-600 c-d-flex c-items-center"
                  >
                    <icon
                      name="Clock"
                      color="var(--c-color-orange-600)"
                      class="ui-mr-1"
                    />
                    Pendente
                  </span>
                  <span
                    v-else
                    class="c-text-b2 c-text-gray-500 c-d-flex c-items-center"
                  >
                    <icon
                      name="X"
                      color="var(--c-color-gray-500)"
                      class="ui-mr-1"
                    />
                    Inativo
                  </span>
                </div>
              </div>
              <div class="ui-w-16 ui-ml-2">
                <div class="c-d-flex c-justify-end ui-space-x-2">
                  <!-- Botão de exclusão -->
                  <button
                    @click="showDeleteEmailModal(email)"
                    class="ui-text-gray-400 hover:ui-text-gray-600 ui-transition-colors"
                    :data-cy="`deleteEmailButton-${index}`"
                  >
                    <icon
                      name="Trash"
                      color="var(--c-color-orange-600)"
                      opacity="0.4"
                    />
                  </button>
                  <!-- Botão de reenvio - apenas para emails pendentes -->
                  <button
                    v-if="email.isPending"
                    @click="resendConfirmationEmail(email)"
                    class="ui-text-gray-400 hover:ui-text-gray-600 ui-transition-colors"
                    :data-cy="`resendEmailButton-${index}`"
                    :disabled="email.resending"
                    :title="email.resending ? 'Reenviando...' : 'Reenviar email de confirmação'"
                  >
                    <icon
                      name="send"
                      color="var(--c-color-orange-600)"
                      opacity="0.4"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </card>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon, CLink, CSkeletonBlock, CSkeletonCircle } from '@estrategiahq/coruja-web-ui';
import Card from '@/components/card';
import AddEmailModal from './add-email.vue';
import DeleteEmailModal from './delete-email.vue';
import { AccountsService } from '@/services/accounts';

export default {
  name: 'CommunicationEmails',
  components: {
    Card,
    Icon,
    CSkeletonBlock,
    CSkeletonCircle,
    CLink,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    userEmail: {
      type: String,
      required: true,
    },
    userId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      communicationEmails: [],
      emailsLoading: false,
      emailsError: null,
      accountsService: null,
    };
  },
  mounted() {
    if (this.userId) {
      this.initializeAccountsService();
    }
  },
  watch: {
    userId: {
      handler(newUserId) {
        if (newUserId && !this.accountsService) {
          this.initializeAccountsService();
        }
      },
      immediate: true,
    },
  },
  methods: {
    initializeAccountsService() {
      this.accountsService = new AccountsService(
        process.env.VUE_APP_ACCOUNTS_URL,
        this.userId,
        this.$router,
      );
      this.loadCommunicationEmails();
    },

    async loadCommunicationEmails() {
      this.emailsLoading = true;
      this.emailsError = null;

      try {
        if (this.accountsService) {
          const response = await this.accountsService.getCommunicationEmails();

          this.communicationEmails = response.map(email => ({
            id: email.id,
            email: email.email,
            inclusionDate: email.created_at ? new Date(email.created_at).toLocaleDateString('pt-BR') : null,
            confirmed: email.is_active && !email.is_pending,
            isActive: email.is_active,
            isPending: email.is_pending,
            activatedAt: email.activated_at ? new Date(email.activated_at).toLocaleDateString('pt-BR') : null,
          }));
        } else {
          this.emailsError = 'Erro ao carregar emails: Serviço não inicializado';
        }
      } catch (error) {
        this.emailsError = 'Erro ao carregar emails';
        this.communicationEmails = [];
      } finally {
        this.emailsLoading = false;
      }
    },

    showAddEmailModal() {
      this.$modal.show({
        component: AddEmailModal,
        props: {},
        events: {
          'email-added': () => {
            this.loadCommunicationEmails();
          },
        },
      });
    },

    showDeleteEmailModal(email) {
      this.$modal.show({
        component: DeleteEmailModal,
        props: {
          email: email,
        },
        events: {
          'email-deleted': () => {
            this.loadCommunicationEmails();
          },
        },
      });
    },

    async resendConfirmationEmail(email) {
      // Marca o email como "reenviando" para mostrar estado de loading
      this.$set(email, 'resending', true);

      try {
        await this.accountsService.resendCommunicationEmail(email.id);

        this.$toast.show({
          type: 'success',
          text: 'Email de confirmação reenviado com sucesso!',
          timeout: 3000,
        });
      } catch (error) {
        this.$toast.show({
          type: 'error',
          text: error.message || 'Erro ao reenviar email de confirmação',
          timeout: 3000,
        });
      } finally {
        // Remove o estado de "reenviando"
        this.$set(email, 'resending', false);
      }
    },
  },
};
</script>
